import { useEffect, useMemo } from "react";
import capitalize from "lodash/capitalize";
import { AgGridReact, toast } from "@jsq/ds/core";
import { ArenaReportTemplateSummarySortField, SortDirection } from "~/graphql/supergraphEnums";
import { useGetArenaReportTemplateSummariesQuery } from "~/graphql/supergraphHooks";
import { formatFullName } from "~/pages/Enablement/AnnouncementList/utils";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { SkeletonOrValueCell } from "./components/SkeletonOrValueCell";
import { ClientTemplateRow } from "./types";

export type { ClientTemplateRow };

export type FetchWorkspacesParams = {
  sortByName?: boolean;
  searchTerm?: string;
};

// Server-side max page size for arenaReportTemplateSummaries
const PAGE_SIZE = 100;

const SKELETON_ROW_COUNT = 50;

const SKELETON_ROWS: ClientTemplateRow[] = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, index) => ({
    id: `skeleton-${index}`,
    clientName: "",
    numberOfTemplates: null,
    lastAccessTimestamp: "",
    lastUpdatedByName: null,
    isSkeletonRow: true,
  }),
);

export const useFetchClientWorkspaces = ({ sortByName, searchTerm }: FetchWorkspacesParams) => {
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();
  const queryResult = useGetArenaReportTemplateSummariesQuery({
    variables: {
      first: PAGE_SIZE,
      sortBy: sortByName
        ? ArenaReportTemplateSummarySortField.ArenaName
        : ArenaReportTemplateSummarySortField.LastUpdatedAt,
      sortDirection: SortDirection.Desc,
      activeOnly: true,
      search: searchTerm || undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const { fetchMore } = queryResult;
  const pageInfo = queryResult.data?.arenaReportTemplateSummaries?.pageInfo;

  // Load remaining pages; each merge updates endCursor, re-running this
  // effect until hasNextPage is false (edges merged in apollo/client.ts).
  useEffect(() => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) {
      return;
    }

    void fetchMore({ variables: { first: PAGE_SIZE, after: pageInfo.endCursor } }).catch(() => {
      toast.error("Error loading client report templates");
    });
  }, [pageInfo?.hasNextPage, pageInfo?.endCursor, fetchMore]);

  const rowData = useMemo(() => {
    if (queryResult.loading) {
      return SKELETON_ROWS;
    }

    return (
      queryResult.data?.arenaReportTemplateSummaries?.edges
        // filter out system templates (arenaId = null) and admin arena (arenaId = 0)
        .filter(
          (edge): edge is typeof edge & { node: { arenaId: number } } =>
            edge.node.arenaId !== null && edge.node.arenaId !== 0,
        )
        .map((edge) => {
          const user = edge.node.lastUpdatedByUser;
          const userName = user
            ? formatFullName(
                capitalize(user.firstName ?? ""),
                capitalize(user.middleName ?? ""),
                capitalize(user.lastName ?? ""),
              )
            : null;
          return {
            id: edge.node.arenaId,
            clientName: edge.node.arenaName,
            numberOfTemplates: edge.node.templateCount,
            lastAccessTimestamp: formatDateWithTimezone(edge.node.lastUpdatedAt ?? undefined),
            lastUpdatedByName: userName,
            isSkeletonRow: false,
          };
        }) ?? []
    );
  }, [queryResult.loading, queryResult.data, formatDateWithTimezone]);

  return { rowData, queryResult };
};

export const useClientTemplatesColumnDefs = () => {
  const columnDefs: AgGridReact.AgGridReactProps<ClientTemplateRow>["columnDefs"] = useMemo(
    () => [
      {
        field: "clientName",
        headerName: "Client name",
        filter: "agTextColumnFilter",
        flex: 2,
        cellRenderer: SkeletonOrValueCell,
      },
      {
        field: "numberOfTemplates",
        headerName: "Number of templates",
        filter: "agNumberColumnFilter",
        flex: 2,
        cellRenderer: SkeletonOrValueCell,
        valueFormatter: (params) => {
          if (params.data?.isSkeletonRow) return "";
          if (params.value === null) return "--";
          return String(params.value);
        },
      },
      {
        field: "lastAccessTimestamp",
        headerName: "Last updated",
        sort: "desc",
        flex: 2,
        cellRenderer: SkeletonOrValueCell,
        valueFormatter: (params) => {
          if (params.data?.isSkeletonRow) return "";
          const timestamp = params.value;
          const userName = params.data?.lastUpdatedByName;
          if (!timestamp) return "--";
          return userName ? `${timestamp} by ${userName}` : timestamp;
        },
      },
    ],
    [],
  );

  return { columnDefs };
};
